import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AddressEnum } from './address.enum';
import { ALL_FORM_FIELDS, nestedFormKeys } from './create-client.constants';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ApiService } from '@core/api.service';
import { TranslateUiModule } from '@core/translation/translate-ui.module';
import { ClientsService } from '@features/clients/clients.service';
import { TranslateService } from '@ngx-translate/core';
import { LetModule } from '@rx-angular/template/let';
import { PushModule } from '@rx-angular/template/push';
import { SegmentService } from '@services/common/segment.service';
import { emailUnitPattern } from '@shared/const/const';
import { allowLettersNumbersApostropheDashSpace, allowNumbersAndModifiersRegExp } from '@shared/const/regexp.const';
import { ValidatorsMessages } from '@shared/const/validators-messages.const';
import { trimValue } from '@shared/utility/trimValue';
import { AppSelectModule } from '@ui-kit/app-select/app-select.module';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { BtnLoaderModule } from '@ui-kit/btn-loader/btn-loader.module';
import { FormControlErrorModule } from '@ui-kit/directives/form-control-error/form-control-error.module';
import { LoaderModule } from '@ui-kit/loader/loader.module';
import { handleError } from '@ui-kit/utility/handleHttpErrorResponse';
import { RegexValidator } from '@ui-kit/validation/utility/regExp';
import { GetEntityModel } from 'collection';
import { Collection, Item } from 'collection';
import { ItemModel } from 'collection';

@Component({
  selector: 'create-client-modal',
  templateUrl: './create-client.component.html',
  styleUrls: ['./create-client.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormControlErrorModule,
    AppSelectModule,
    BtnLoaderModule,
    LoaderModule,
    MatTooltipModule,
    LetModule,
    PushModule,
    TranslateUiModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateClientComponent implements OnInit {
  formGroup: UntypedFormGroup;
  formKeys = nestedFormKeys;
  clientSettingsItem: ItemModel;
  clientItem: Item;
  selectorToFocus = 'textArea,mat-select,select,input,button';
  countriesCollection = new Collection({
    api: this._api.countries
  });

  addressEnum = AddressEnum;

  shippingCountryModel: any = null;
  billingCountryModel: any = null;

  getAvailableNumberRequest: GetEntityModel;

  @ViewChild('clientNameInput', { static: false }) clientNameInput: ElementRef;

  constructor(
    private _clientsService: ClientsService,
    private _api: ApiService,
    private _toastService: ToasterService,
    private _translate: TranslateService,
    private _elemRef: ElementRef,
    private _segmentService: SegmentService,
    private _dialogRef: MatDialogRef<any>,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    @Inject(DOCUMENT) private document: Document,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.clientItem = new Item({
      api: this._api.clients,
      params: {
        expand: 'full_name'
      }
    });

    this.getAvailableNumberRequest = this._clientsService.getAvailableNumberRequest;
  }

  updateCountryModel(type, eventModel) {
    switch (type) {
      case AddressEnum.Shipping:
        this.shippingCountryModel = eventModel.model;
        break;
      case AddressEnum.Billing:
        this.billingCountryModel = eventModel.model;
        break;
    }
  }

  copyAddress(fromType) {
    switch (fromType) {
      case AddressEnum.Shipping:
        this.billingForm.setValue(this.shippingForm.value);
        this.billingCountryModel = this.shippingCountryModel;
        break;
      case AddressEnum.Billing:
        this.shippingForm.setValue(this.billingForm.value);
        this.shippingCountryModel = this.billingCountryModel;
        break;
    }
  }

  get shippingForm(): AbstractControl {
    return this.formGroup.get('shipping_address');
  }

  get billingForm(): AbstractControl {
    return this.formGroup.get('billing_address');
  }

  getCountriesList({ search, page }): void {
    this.countriesCollection.setParams('page', page);
    search.length ? this.countriesCollection.setParams('name', search) : this.countriesCollection.clearParams('name');
    this.countriesCollection.search();
  }

  private initSegmentEvent(client, name: string): void {
    this._segmentService.track(name, {
      client_name: client.name,
      client_status: client.status
    });
  }

  close(): void {
    this._dialogRef.close();
  }

  private createClient(): void {
    this.clientItem.createItem({ data: this.formGroup.value }).subscribe(
      (res: { data: any }) => {
        this.data.subject.next({
          confirmed: true,
          modal: this._dialogRef,
          item: res.data
        });

        this.close();

        this.initSegmentEvent(res.data, 'Client Create');

        this._toastService.show({
          type: 'success',
          message: marker('Client is successfully created')
        });
      },
      (err) => {
        const errorResponse = err.error;
        handleError(errorResponse, this._toastService);
      }
    );
  }

  private initForm(): void {
    this.formGroup = new UntypedFormGroup({
      display_id: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(15)]),
      name: new UntypedFormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
      contact_person: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(70)]),
      email: new UntypedFormControl('', [RegexValidator(emailUnitPattern, this._translate.instant(marker('E-mail is invalid')))]),
      phone: new UntypedFormControl('', [
        Validators.minLength(4),
        Validators.maxLength(18),
        RegexValidator(allowNumbersAndModifiersRegExp, this._translate.instant(marker(ValidatorsMessages.phoneInvalid)))
      ]),
      billing_address: new UntypedFormGroup({
        country_id: new UntypedFormControl(''),
        street: new UntypedFormControl(''),
        city: new UntypedFormControl(''),
        state: new UntypedFormControl('', [
          RegexValidator(
            allowLettersNumbersApostropheDashSpace,
            this._translate.instant(
              marker("Please enter a value in the correct format: letters, numbers (using point), ', -, space")
            )
          )
        ]),
        zip: new UntypedFormControl('', [Validators.minLength(2), Validators.maxLength(11)])
      }),
      shipping_address: new UntypedFormGroup({
        country_id: new UntypedFormControl(''),
        street: new UntypedFormControl(''),
        city: new UntypedFormControl(''),
        state: new UntypedFormControl('', [
          RegexValidator(
            allowLettersNumbersApostropheDashSpace,
            this._translate.instant(
              marker("Please enter a value in the correct format: letters, numbers (using point), ', -, space")
            )
          )
        ]),
        zip: new UntypedFormControl('', [Validators.minLength(2), Validators.maxLength(11)])
      })
    });
  }

  submit(): void {
    if (this.formGroup.valid) {
      this.formGroup.value.phone = trimValue(this.formGroup?.value?.phone);
      this.createClient();
    } else {
      this.formGroup.markAllAsTouched();

      const formGroupInvalid = this._elemRef.nativeElement.querySelectorAll('.ng-invalid,:not(.mat-badge-hidden).mat-badge');
      const elementToOffset = this.getElementToOffset(formGroupInvalid[1]);
      this.document.documentElement.scrollTop = elementToOffset.offsetTop;
      this.setFocusOnError(elementToOffset);
    }
  }

  private getElementToOffset(element: any) {
    const defaultElement = element;
    while (!(element.parentElement instanceof HTMLFormElement)) {
      if (element.parentElement) {
        element = element.parentElement;
      } else {
        return defaultElement;
      }
    }
    return element;
  }

  private setFocusOnError(elementToOffset: any) {
    const listElementos = elementToOffset.querySelectorAll(this.selectorToFocus);
    if (listElementos.length > 0) {
      listElementos[0].focus();
    }
  }

  private focusNameInput(): void {
    setTimeout(() => {
      this.clientNameInput.nativeElement.focus();
    }, 400);
  }

  ngOnInit(): void {
    this.initForm();
    this.getAvailableNumberRequest.get().subscribe((res) => {
      this.formGroup.get(ALL_FORM_FIELDS.display_id).setValue(res.data);
    });

    this.focusNameInput();
  }
}
